import React, { useState } from 'react';
import { 
  FaRupeeSign, 
  FaPercent, 
  FaCalendar, 
  FaChartLine, 
  FaInfoCircle, 
  FaQuestionCircle, 
  FaCheck, 
  FaStar,
  FaBookOpen,
  FaTools,
  FaLightbulb,
  FaExclamationTriangle,
  FaMoneyBill,
  FaFile,
  FaCreditCard,
  FaClock,
  FaExchangeAlt,
  FaCalendarCheck
} from 'react-icons/fa';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const HomeLoanEMICalculator = () => {
  const [loanAmount, setLoanAmount] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [loanTenure, setLoanTenure] = useState('');
  const [emi, setEMI] = useState('0.00');
  const [totalInterest, setTotalInterest] = useState('0.00');
  const [totalAmount, setTotalAmount] = useState('0.00');
  const [monthlyBreakdown, setMonthlyBreakdown] = useState([]);
  const [error, setError] = useState('');

  const calculateEMI = () => {
    if (!loanAmount || !interestRate || !loanTenure) {
      setError('Please fill in all fields');
      return;
    }

    const P = parseFloat(loanAmount);
    const r = parseFloat(interestRate) / 12 / 100;
    const n = parseFloat(loanTenure) * 12;

    if (isNaN(P) || isNaN(r) || isNaN(n)) {
      setError('Please enter valid numbers');
      return;
    }

    if (P <= 0 || r <= 0 || n <= 0) {
      setError('Please enter positive values');
      return;
    }

    const emiValue = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    if (isNaN(emiValue)) {
      setError('Calculation error. Please check your inputs.');
      return;
    }

    setEMI(emiValue.toFixed(2));
    setError('');

    const totalInterestValue = (emiValue * n) - P;
    setTotalInterest(totalInterestValue.toFixed(2));

    const totalAmountValue = emiValue * n;
    setTotalAmount(totalAmountValue.toFixed(2));

    const breakdown = [];
    let remainingPrincipal = P;
    for (let i = 0; i < n; i++) {
      const interest = remainingPrincipal * r;
      const principal = emiValue - interest;
      remainingPrincipal -= principal;
      breakdown.push({
        month: i + 1,
        interest: interest.toFixed(2),
        principal: principal.toFixed(2),
        remainingPrincipal: remainingPrincipal.toFixed(2)
      });
    }
    setMonthlyBreakdown(breakdown);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const faqItems = [
    {
      question: "What is EMI?",
      answer: "EMI stands for Equated Monthly Installment. It is a fixed amount that a borrower pays to the lender every month to repay the loan. This amount includes both principal and interest components. The EMI remains constant throughout the loan tenure, making it easier for borrowers to plan their monthly expenses.",
      icon: FaRupeeSign,
      color: "text-blue-500"
    },
    {
      question: "How is EMI calculated?",
      answer: "EMI is calculated using the formula: EMI = [P x R x (1+R)^N]/[(1+R)^N-1], where:\nP = Principal loan amount\nR = Monthly interest rate (annual rate divided by 12)\nN = Number of monthly installments\nThe calculator uses this formula to provide accurate EMI calculations.",
      icon: FaChartLine,
      color: "text-purple-500"
    },
    {
      question: "What factors affect my EMI?",
      answer: "Your EMI is affected by three main factors:\n1. Loan amount (principal) - Higher amount means higher EMI\n2. Interest rate - Higher rate increases EMI\n3. Loan tenure (duration) - Longer tenure reduces EMI but increases total interest\nOther factors like processing fees and insurance can also affect the final EMI amount.",
      icon: FaPercent,
      color: "text-green-500"
    },
    {
      question: "How can I reduce my EMI?",
      answer: "You can reduce your EMI by:\n1. Increasing the loan tenure (though this increases total interest)\n2. Negotiating a lower interest rate with your lender\n3. Making part-prepayments to reduce the principal amount\n4. Choosing a fixed interest rate if market rates are expected to rise\nHowever, keep in mind that increasing tenure will increase the total interest paid over the loan period.",
      icon: FaCalendar,
      color: "text-orange-500"
    },
    {
      question: "What is the benefit of using this calculator?",
      answer: "This calculator helps you:\n1. Plan your monthly budget with accurate EMI calculations\n2. Compare different loan scenarios\n3. Understand how changes in loan terms affect your payments\n4. Make informed decisions about your home loan\n5. Plan your financial goals better by seeing the total interest and repayment amount", 
      icon: FaLightbulb,
      color: "text-yellow-500"
    },
    {
      question: "How accurate are the calculations?",
      answer: "The calculator uses standard EMI calculation formulas and provides accurate results based on the inputs you provide. However, actual EMI amounts may vary slightly due to:\n1. Processing fees\n2. Insurance charges\n3. Prepayment penalties\n4. Changes in interest rates\n5. Additional charges by the lender", 
      icon: FaCheck,
      color: "text-green-500"
    },
    {
      question: "What is the difference between principal and interest?",
      answer: "Principal is the actual loan amount borrowed, while interest is the cost of borrowing that amount. In the initial years of your loan, a larger portion of your EMI goes towards interest, while in later years, more goes towards reducing the principal. This is why prepaying your loan early can save you significant interest costs.",
      icon: FaBookOpen,
      color: "text-blue-500"
    },
    {
      question: "How does loan tenure affect my EMI?",
      answer: "Longer loan tenure results in:\n1. Lower EMIs\n2. Higher total interest paid\n3. Longer repayment period\nShorter tenure means:\n1. Higher EMIs\n2. Lower total interest\n3. Faster loan repayment\nChoose a tenure that balances your monthly affordability with overall cost.",
      icon: FaCalendar,
      color: "text-orange-500"
    },
    {
      question: "What happens if I miss an EMI payment?",
      answer: "Missing an EMI payment can lead to:\n1. Late payment fees\n2. Increased interest rates\n3. Negative impact on your credit score\n4. Legal notices from the lender\n5. Possibility of loan foreclosure\nIt's essential to maintain regular payments to avoid these consequences. If you're facing financial difficulties, contact your lender immediately to discuss options.",
      icon: FaExclamationTriangle,
      color: "text-red-500"
    },
    {
      question: "Can I prepay my home loan?",
      answer: "Yes, most home loans allow prepayment. Prepaying your loan can:\n1. Reduce the total interest paid\n2. Shorten the loan tenure\n3. Lower your future EMIs\nHowever, check with your lender about:\n1. Any prepayment penalties\n2. Minimum prepayment amount\n3. Frequency of prepayments\n4. Documentation required", 
      icon: FaMoneyBill,
      color: "text-green-500"
    },
    {
      question: "What documents do I need for a home loan?",
      answer: "Commonly required documents include:\n1. Identity proof (Aadhaar/PAN)\n2. Address proof\n3. Income proof (salary slips, IT returns)\n4. Bank statements\n5. Property documents\n6. Loan application form\n7. Photographs\nRequirements may vary between lenders, so check with your specific lender for their requirements.",
      icon: FaFile,
      color: "text-purple-500"
    },
    {
      question: "What is the maximum loan amount I can get?",
      answer: "The maximum loan amount depends on:\n1. Your income (typically 40-60% of net income)\n2. Credit score\n3. Age\n4. Employment stability\n5. Property value\n6. Lender's policies\nTypically, banks offer up to 80-90% of the property value as a home loan. The exact amount will be determined by your lender based on your financial profile.",
      icon: FaRupeeSign,
      color: "text-blue-500"
    },
    {
      question: "How does my credit score affect the loan?",
      answer: "Your credit score impacts your loan in several ways:\n1. Higher score = Better interest rates\n2. Higher score = Higher loan amount\n3. Higher score = Better negotiation power\n4. Lower score = Higher interest rates\n5. Lower score = Lower loan amount\n6. Lower score = Higher chance of loan rejection\nMaintain a good credit score by paying bills on time and maintaining healthy credit utilization.",
      icon: FaCreditCard,
      color: "text-purple-500"
    },
    {
      question: "What is a floating interest rate?",
      answer: "A floating interest rate:\n1. Changes based on market conditions\n2. Can go up or down during the loan tenure\n3. Affects your EMI amount\n4. Is usually lower than fixed rates initially\n5. Carries more risk due to market fluctuations\nThis is different from a fixed interest rate which remains constant throughout the loan tenure.",
      icon: FaChartLine,
      color: "text-green-500"
    },
    {
      question: "Can I change my loan tenure after taking the loan?",
      answer: "Yes, most lenders allow you to:\n1. Extend your loan tenure (lower EMIs, higher interest)\n2. Reduce your loan tenure (higher EMIs, lower interest)\nHowever, there might be:\n1. Processing fees\n2. Documentation requirements\n3. Minimum tenure changes\n4. Approval process\nCheck with your lender about their specific policies and fees.",
      icon: FaCalendar,
      color: "text-orange-500"
    },
    {
      question: "What is a moratorium period?",
      answer: "A moratorium period is:\n1. A grace period during which you don't have to make EMI payments\n2. Usually offered at the start of the loan tenure\n3. Can range from 1-6 months\n4. Interest continues to accrue during this period\n5. Adds to your loan amount\nCheck with your lender about their moratorium period policies and conditions.",
      icon: FaClock,
      color: "text-yellow-500"
    },
    {
      question: "What is a balance transfer?",
      answer: "A balance transfer is when you:\n1. Transfer your existing home loan to another lender\n2. Get better interest rates or terms\n3. Reduce your EMI or total interest paid\n4. May need to pay transfer fees\n5. Need to meet the new lender's eligibility criteria\nBenefits include lower interest rates and better terms, but check for any associated costs.",
      icon: FaExchangeAlt,
      color: "text-blue-500"
    },
    {
      question: "How often should I review my home loan?",
      answer: "It's a good practice to review your home loan:\n1. Annually for interest rate changes\n2. When market rates drop\n3. When your financial situation changes\n4. Before making prepayments\n5. Before changing loan tenure\nThis helps you:\n1. Stay updated about interest rates\n2. Consider prepayment options\n3. Plan your finances better\n4. Make informed decisions about your loan", 
      icon: FaCalendarCheck,
      color: "text-green-500"
    }
  ];

  const benefits = [
    {
      title: "Financial Planning",
      description: "Plan your monthly budget with accurate EMI calculations",
      icon: FaBookOpen,
      color: "text-blue-500"
    },
    {
      title: "Quick Calculations",
      description: "Get instant results with just a few clicks",
      icon: FaTools,
      color: "text-purple-500"
    },
    {
      title: "Detailed Breakdown",
      description: "See how your payments are split between principal and interest",
      icon: FaChartLine,
      color: "text-green-500"
    },
    {
      title: "Save Money",
      description: "Plan your loan tenure to minimize total interest paid",
      icon: FaStar,
      color: "text-yellow-500"
    },
    {
      title: "Educational Tool",
      description: "Learn about loan calculations and financial planning",
      icon: FaLightbulb,
      color: "text-orange-500"
    },
    {
      title: "User Friendly",
      description: "Easy to use interface with clear visualizations",
      icon: FaQuestionCircle,
      color: "text-blue-500"
    }
  ];

  const howToUse = [
    {
      title: "Step 1: Enter Loan Details",
      description: "Input your loan amount, interest rate, and loan tenure in years. Make sure to enter valid numbers for accurate calculations."
    },
    {
      title: "Step 2: Calculate EMI",
      description: "Click the 'Calculate EMI' button to get your monthly EMI amount, total interest, and total amount payable."
    },
    {
      title: "Step 3: Review Breakdown",
      description: "Check the payment breakdown chart to see how your monthly payments are split between principal and interest over time."
    },
    {
      title: "Step 4: Use the Information",
      description: "Use this information to plan your finances and make informed decisions about your home loan."
    }
  ];

  const features = [
    {
      icon: FaTools,
      title: "Quick Calculations",
      description: "Get instant EMI calculations with just a few clicks."
    },
    {
      icon: FaChartLine,
      title: "Visual Breakdown",
      description: "See a clear visual representation of your payment schedule."
    },
    {
      icon: FaBookOpen,
      title: "Detailed Information",
      description: "Access comprehensive FAQs and usage instructions."
    },
    {
      icon: FaLightbulb,
      title: "Financial Planning",
      description: "Plan your finances better with accurate EMI calculations."
    }
  ];

  const chartData = {
    labels: Array.from({ length: monthlyBreakdown.length }, (_, i) => `Month ${i + 1}`),
    datasets: [
      {
        label: 'Interest Payment',
        data: monthlyBreakdown.map(item => item.interest),
        borderColor: '#ff6b6b',
        tension: 0.1,
        fill: true,
        backgroundColor: 'rgba(255, 107, 107, 0.1)'
      },
      {
        label: 'Principal Payment',
        data: monthlyBreakdown.map(item => item.principal),
        borderColor: '#4ecdc4',
        tension: 0.1,
        fill: true,
        backgroundColor: 'rgba(78, 205, 196, 0.1)'
      }
    ],
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-purple-50 min-h-screen p-6">
      <Helmet>
        <title>Home Loan EMI Calculator</title>
        <meta name="description" content="Calculate your home loan EMI, total interest, and payment breakdown with our easy-to-use calculator." />
        <meta property="og:title" content="Home Loan EMI Calculator" />
        <meta property="og:description" content="Calculate your home loan EMI, total interest, and payment breakdown with our easy-to-use calculator." />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold text-gray-800">
            <FaChartLine className="inline-block mr-2 text-blue-500" />
            Home Loan EMI Calculator
          </h2>
          <div className="flex items-center space-x-4">
            <button 
              className="text-blue-500 hover:text-blue-600 transition-colors duration-200"
              title="Help"
            >
              <FaQuestionCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-blue-50 p-4 rounded-xl">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Loan Amount (₹)
            </label>
            <div className="relative">
              <FaRupeeSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="number"
                value={loanAmount}
                onChange={(e) => setLoanAmount(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                placeholder="Enter loan amount"
                min="0"
              />
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-xl">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Interest Rate (% p.a.)
            </label>
            <div className="relative">
              <FaPercent className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="number"
                value={interestRate}
                onChange={(e) => setInterestRate(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                placeholder="Enter interest rate"
                min="0"
                max="20"
              />
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-xl">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Loan Tenure (years)
            </label>
            <div className="relative">
              <FaCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="number"
                value={loanTenure}
                onChange={(e) => setLoanTenure(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                placeholder="Enter loan tenure"
                min="1"
                max="30"
              />
            </div>
          </div>
        </div>

        <button
          onClick={calculateEMI}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300 mb-8"
        >
          Calculate EMI
        </button>

        <div className="space-y-6">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <h2 className="text-xl font-semibold mb-4">Your EMI Details</h2>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4">
                {error}
              </div>
            )}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Monthly EMI</span>
                <span className="font-semibold text-blue-600">
                  {formatCurrency(parseFloat(emi))}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Interest</span>
                <span className="font-semibold text-red-600">
                  {formatCurrency(parseFloat(totalInterest))}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Amount</span>
                <span className="font-semibold text-green-600">
                  {formatCurrency(parseFloat(totalAmount))}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <h2 className="text-xl font-semibold mb-4">Payment Breakdown</h2>
            <Line data={chartData} className="h-80" />
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <h2 className="text-xl font-semibold mb-4">Features</h2>
            <div className="grid grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div key={index} className="p-4 border rounded-lg">
                  <div className="text-2xl mb-2">
                    <feature.icon className="text-blue-500" />
                  </div>
                  <h3 className="font-semibold mb-1">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <h2 className="text-xl font-semibold mb-4">How to Use</h2>
            <div className="space-y-4">
              {howToUse.map((step, index) => (
                <div key={index} className="border-l-4 border-blue-500 pl-4">
                  <h3 className="font-semibold mb-1">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <h2 className="text-xl font-semibold mb-4">FAQs</h2>
            <div className="space-y-4">
              {faqItems.map((faq, index) => (
                <div key={index} className="border-l-4 border-blue-500 pl-4 bg-blue-50/10 rounded-lg p-3">
                  <div className="flex items-center mb-2">
                    <faq.icon className={`${faq.color} w-6 h-6 mr-2`} />
                    <h3 className="font-semibold text-gray-800">{faq.question}</h3>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md">
            <h2 className="text-xl font-semibold mb-4">Benefits</h2>
            <div className="grid grid-cols-2 gap-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="p-4 border rounded-lg">
                  <div className="flex items-center mb-2">
                    <benefit.icon className={`${benefit.color} w-6 h-6 mr-2`} />
                    <h3 className="font-semibold">{benefit.title}</h3>
                  </div>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeLoanEMICalculator;
